import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, Plus, Phone, Mail, ChevronLeft, ChevronRight, Archive } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", first_name: "", last_name: "", email: "", phone: "", date_of_birth: "" });
  const navigate = useNavigate();
  const limit = 100;

  const load = async (q = search, p = page, archived = showArchived) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients?search=${q}&page=${p}&limit=${limit}&archived=${archived}`);
      setPatients(data.patients);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [page, showArchived]);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); load(e.target.value, 1, showArchived); };
  const totalPages = Math.ceil(total / limit);

  const handleCreate = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/patients", form); navigate(`/app/patients/${data.id}`); } catch {}
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="patients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Patients</h1>
          <p className="text-sm text-[#757675]">{total} {showArchived ? "archived" : ""} patients</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowArchived(!showArchived); setPage(1); }} data-testid="archive-toggle"
            className={`text-[11px] font-medium px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${showArchived ? "bg-[#205B5A] text-white border-[#205B5A]" : "text-[#757675] border-[#1A1B1A]/10 hover:border-[#1A1B1A]/20"}`}>
            <Archive className="w-3 h-3" /> {showArchived ? "Showing archived" : "Archived"}
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="add-patient-btn"
            className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] transition-colors flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Add patient
          </button>
        </div>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#757675]" />
        <input value={search} onChange={handleSearch} data-testid="patient-search-input"
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
          placeholder="Search by name, phone, email, DOB, or reference..." />
      </div>

      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden" data-testid="patients-table">
        <table className="w-full">
          <thead><tr className="border-b border-[#1A1B1A]/[0.06]">
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase tracking-wider">Name</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase tracking-wider hidden md:table-cell">Contact</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase tracking-wider hidden lg:table-cell">DOB</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase tracking-wider hidden lg:table-cell">Ref</th>
          </tr></thead>
          <tbody>
            {patients.map((p) => (
              <tr key={p.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer transition-colors" onClick={() => navigate(`/app/patients/${p.id}`)}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-semibold text-[#205B5A]">{p.first_name?.[0]}{p.last_name?.[0]}</span>
                    </div>
                    <div><span className="text-sm font-medium text-[#1A1B1A]">{p.title ? `${p.title} ` : ""}{p.first_name} {p.last_name}</span>{p.preferred_name && <p className="text-[10px] text-[#757675]">Prefers: {p.preferred_name}</p>}</div>
                  </div>
                </td>
                <td className="px-5 py-3 hidden md:table-cell">
                  <div className="space-y-0.5">
                    {p.email && <p className="text-xs text-[#757675] flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</p>}
                    {p.phone && <p className="text-xs text-[#757675] flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</p>}
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-[#757675] hidden lg:table-cell">{p.date_of_birth || "—"}</td>
                <td className="px-5 py-3 text-sm text-[#757675] hidden lg:table-cell font-mono">{p.reference_number || "—"}</td>
              </tr>
            ))}
            {patients.length === 0 && !loading && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-[#757675]">{showArchived ? "No archived patients" : "No patients found"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#757675]">Page {page} of {totalPages}</p>
          <div className="flex gap-1.5">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="prev-page"
              className="p-1.5 rounded-lg border border-[#1A1B1A]/10 hover:bg-[#FAF9F6] disabled:opacity-30"><ChevronLeft className="w-4 h-4 text-[#4A4B4A]" /></button>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} data-testid="next-page"
              className="p-1.5 rounded-lg border border-[#1A1B1A]/10 hover:bg-[#FAF9F6] disabled:opacity-30"><ChevronRight className="w-4 h-4 text-[#4A4B4A]" /></button>
          </div>
        </div>
      )}

      {/* Quick Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="create-patient-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>New patient</h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <select value={form.title} aria-label="Title" onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">
                <option value="">Title (optional)</option>{["Mr", "Mrs", "Ms", "Dr", "Prof"].map(t => <option key={t}>{t}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required placeholder="First name" aria-label="First name" data-testid="patient-first-name"
                  className="px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                <input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required placeholder="Last name" aria-label="Last name" data-testid="patient-last-name"
                  className="px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              </div>
              <input value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} type="date" aria-label="Date of birth" data-testid="patient-dob" placeholder="Date of birth"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone" aria-label="Phone" data-testid="patient-phone"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" placeholder="Email" aria-label="Email" data-testid="patient-email"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
                <button type="submit" data-testid="save-patient-btn" className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">Create patient</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
